import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FaCalendarAlt,
  FaList,
  FaPlus,
  FaSearch,
  FaSpinner,
  FaThLarge,
  FaTrashAlt,
} from "react-icons/fa";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { MatchFilters } from "@/components/matches/MatchFilters";
import { cn } from "@/lib/utils";

export type MatchViewMode = "grid" | "list";

export interface MatchFiltersToolbarProps {
  filters: MatchFilters;
  onFiltersChange: (filters: MatchFilters) => void;
  onReset: () => void;
  years: number[];
  viewMode: MatchViewMode;
  onViewModeChange: (mode: MatchViewMode) => void;
  selectedCount: number;
  filteredCount: number;
  totalCount: number;
  showFilters: boolean;
  showSelectAll: boolean;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onBulkDelete: () => void;
  onRegisterMatch: () => void;
  isProcessing?: boolean;
}

function FilterChip({
  label,
  onRemove,
  removeLabel,
}: {
  label: string;
  onRemove?: () => void;
  removeLabel: string;
}) {
  return (
    <Badge variant="secondary" className="gap-1 pr-1 font-normal">
      {label}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="ml-0.5 rounded-sm px-1 text-xs hover:bg-background/80"
          aria-label={removeLabel}
        >
          ×
        </button>
      )}
    </Badge>
  );
}

export function MatchFiltersToolbar({
  filters,
  onFiltersChange,
  onReset,
  years,
  viewMode,
  onViewModeChange,
  selectedCount,
  filteredCount,
  totalCount,
  showFilters,
  showSelectAll,
  onSelectAll,
  onClearSelection,
  onBulkDelete,
  onRegisterMatch,
  isProcessing = false,
}: MatchFiltersToolbarProps) {
  const { t } = useTranslation();
  const [datesOpen, setDatesOpen] = useState(false);

  const updateFilters = (partial: Partial<MatchFilters>) => {
    onFiltersChange({ ...filters, ...partial });
  };

  const hasDateFilter = Boolean(filters.dateRange?.start && filters.dateRange?.end);
  const hasSortFilter = filters.sortBy !== "date-desc";
  const hasSearchFilter = Boolean(filters.search.trim());
  const hasActiveFilters = hasSearchFilter || hasDateFilter || hasSortFilter;

  const filterButtonClass = (active: boolean) =>
    cn(
      "h-9 border-dashed px-3",
      active && "border-soccer-primary/50 bg-soccer-primary/5 text-foreground"
    );

  return (
    <div className="space-y-3 rounded-xl border bg-card p-3 shadow-sm sm:p-4">
      {/* Search + view + primary CTA */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        {showFilters && (
          <div className="relative min-w-0 flex-1">
            <FaSearch className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={filters.search}
              onChange={(e) => updateFilters({ search: e.target.value })}
              placeholder={t("matches.searchPlayers")}
              className="h-10 pl-9"
            />
          </div>
        )}

        <div
          className={cn(
            "flex items-center gap-2",
            showFilters ? "sm:shrink-0" : "w-full sm:ml-auto sm:w-auto sm:justify-end"
          )}
        >
          {showFilters && (
            <div
              role="group"
              aria-label={t("v2.matches.viewMode", "View mode")}
              className="inline-flex h-10 items-center rounded-lg border bg-muted/40 p-0.5"
            >
              {(
                [
                  { mode: "grid" as const, icon: FaThLarge, label: t("v2.matches.gridView", "Grid") },
                  { mode: "list" as const, icon: FaList, label: t("v2.matches.listView", "List") },
                ] as const
              ).map(({ mode, icon: Icon, label }) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => onViewModeChange(mode)}
                  aria-pressed={viewMode === mode}
                  className={cn(
                    "inline-flex h-full items-center gap-2 rounded-md px-3 text-xs font-medium transition-colors",
                    viewMode === mode
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  <Icon className="h-3.5 w-3.5 shrink-0" />
                  <span>{label}</span>
                </button>
              ))}
            </div>
          )}

          <Button
            type="button"
            variant="default"
            size="sm"
            className={cn("h-10 shrink-0 px-3 text-xs font-semibold", !showFilters && "w-full sm:w-auto")}
            onClick={onRegisterMatch}
            icon={<FaPlus className="h-3.5 w-3.5" />}
          >
            {t("v2.matches.registerMatch")}
          </Button>
        </div>
      </div>

      {showFilters && (
        <div className="flex flex-wrap items-center gap-2">
          <Popover open={datesOpen} onOpenChange={setDatesOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={filterButtonClass(hasDateFilter)}
                icon={<FaCalendarAlt className="h-3.5 w-3.5" />}
              >
                {t("matches.dateRange", "Date range")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[min(100vw-2rem,300px)] p-0" align="start">
              <div className="space-y-3 p-3">
                <Label className="text-xs text-muted-foreground">
                  {t("matches.selectDateRange", "Select date range")}
                </Label>
                <div className="space-y-2">
                  <div>
                    <Label htmlFor="match-filter-start" className="text-xs">
                      {t("v2.matches.dateFrom", "From")}
                    </Label>
                    <Input
                      id="match-filter-start"
                      type="date"
                      value={filters.dateRange?.start ?? ""}
                      onChange={(e) =>
                        updateFilters({
                          dateRange: {
                            start: e.target.value || null,
                            end: filters.dateRange?.end ?? null,
                          },
                        })
                      }
                      className="mt-1 h-9"
                    />
                  </div>
                  <div>
                    <Label htmlFor="match-filter-end" className="text-xs">
                      {t("v2.matches.dateTo", "To")}
                    </Label>
                    <Input
                      id="match-filter-end"
                      type="date"
                      value={filters.dateRange?.end ?? ""}
                      onChange={(e) =>
                        updateFilters({
                          dateRange: {
                            start: filters.dateRange?.start ?? null,
                            end: e.target.value || null,
                          },
                        })
                      }
                      className="mt-1 h-9"
                    />
                  </div>
                </div>
                {years.length > 0 && (
                  <div>
                    <Label className="text-xs text-muted-foreground">
                      {t("matches.quickYear", "Quick year")}
                    </Label>
                    <Select
                      value=""
                      onValueChange={(year) => {
                        updateFilters({
                          dateRange: { start: `${year}-01-01`, end: `${year}-12-31` },
                        });
                        setDatesOpen(false);
                      }}
                    >
                      <SelectTrigger className="mt-1 h-9">
                        <SelectValue placeholder={t("matches.selectYear", "Select year")} />
                      </SelectTrigger>
                      <SelectContent>
                        {years.map((year) => (
                          <SelectItem key={year} value={String(year)}>
                            {year}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <div className="flex justify-between border-t p-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => updateFilters({ dateRange: { start: null, end: null } })}
                >
                  {t("matches.clear", "Clear")}
                </Button>
                <Button type="button" size="sm" onClick={() => setDatesOpen(false)}>
                  {t("matches.apply", "Apply")}
                </Button>
              </div>
            </PopoverContent>
          </Popover>

          <Select
            value={filters.sortBy}
            onValueChange={(value: MatchFilters["sortBy"]) => updateFilters({ sortBy: value })}
          >
            <SelectTrigger
              className={cn("h-9 w-[148px] border-dashed", hasSortFilter && "border-soccer-primary/50")}
            >
              <SelectValue placeholder={t("matches.sortBy", "Sort by")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="date-desc">{t("matches.newestFirst", "Newest first")}</SelectItem>
              <SelectItem value="date-asc">{t("matches.oldestFirst", "Oldest first")}</SelectItem>
            </SelectContent>
          </Select>

          {showSelectAll && selectedCount === 0 && (
            <Button type="button" variant="ghost" size="sm" className="h-9" onClick={onSelectAll}>
              {t("matches.selectAll")}
            </Button>
          )}

          <span className="ml-auto text-xs text-muted-foreground">
            {t("v2.matches.showingCount", "{{filtered}} of {{total}} matches", {
              filtered: filteredCount,
              total: totalCount,
            })}
          </span>
        </div>
      )}

      {selectedCount > 0 && (
        <div className="flex flex-col gap-2 rounded-lg border border-soccer-primary/30 bg-soccer-primary/5 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm font-medium">
            {t("v2.matches.toolbarSelection", "{{count}} selected", {
              count: selectedCount,
            })}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8"
              onClick={onClearSelection}
              disabled={isProcessing}
            >
              {t("matches.clearSelection", "Clear selection")}
            </Button>
            {showSelectAll && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8"
                onClick={onSelectAll}
                disabled={isProcessing}
              >
                {t("matches.selectAll")}
              </Button>
            )}
            <Button
              type="button"
              variant="destructive"
              size="sm"
              className="h-8"
              onClick={onBulkDelete}
              disabled={isProcessing}
            >
              {isProcessing ? (
                <FaSpinner className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <FaTrashAlt className="mr-1.5 h-3.5 w-3.5" />
              )}
              {t("matches.deleteSelected", "Delete ({{count}}) selected", {
                count: selectedCount,
              })}
            </Button>
          </div>
        </div>
      )}

      {showFilters && hasActiveFilters && selectedCount === 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">
            {t("v2.matches.activeFilters", "Active filters")}:
          </span>
          {hasSearchFilter && (
            <FilterChip
              label={`${t("common.search", "Search")}: ${filters.search}`}
              onRemove={() => updateFilters({ search: "" })}
              removeLabel={t("matches.clearFilters", "Clear filters")}
            />
          )}
          {hasDateFilter && filters.dateRange?.start && filters.dateRange?.end && (
            <FilterChip
              label={`${filters.dateRange.start} – ${filters.dateRange.end}`}
              onRemove={() => updateFilters({ dateRange: { start: null, end: null } })}
              removeLabel={t("matches.clearFilters", "Clear filters")}
            />
          )}
          {hasSortFilter && (
            <FilterChip
              label={
                filters.sortBy === "date-asc"
                  ? t("matches.oldestFirst", "Oldest first")
                  : t("matches.newestFirst", "Newest first")
              }
              onRemove={() => updateFilters({ sortBy: "date-desc" })}
              removeLabel={t("matches.clearFilters", "Clear filters")}
            />
          )}
          <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={onReset}>
            {t("matches.clearAllFilters", "Clear all filters")}
          </Button>
        </div>
      )}
    </div>
  );
}
